import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  /** 현재 선택된 skill UUID 배열. */
  value: string[];
  onChange: (next: string[]) => void;
  /** 동시 선택 최대치. */
  max?: number;
}

/**
 * 외부 노출 에이전트의 backing skill 다중 선택 — 토큰-인풋 + 자동완성 + 키보드 nav.
 *
 * 패턴은 PairsWellWithCombo 와 동일 (GitHub label / Linear assignee picker). 차이점은
 * value 가 slug 가 아니라 skill UUID 라는 점.
 *
 * 후보 출처: `api.adminSkillList()` 의 enabled skill 목록. 이미 선택된 항목 제외.
 */
export function BackingSkillsCombo({ value, onChange, max = 4 }: Props) {
  const query = useQuery({
    queryKey: ['admin', 'skills'],
    queryFn: () => api.adminSkillList(),
  });

  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 외부 클릭 시 dropdown 닫기.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setText('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // 후보 = enabled && (value 에 없음). query 가 있으면 substring 매칭.
  const candidates = useMemo(() => {
    const all = query.data?.skills ?? [];
    const selected = new Set(value);
    const q = text.trim().toLowerCase();
    return all
      .filter((s) => s.enabled && !selected.has(s.id))
      .filter((s) => {
        if (!q) return true;
        return s.slug.toLowerCase().includes(q) || s.name.toLowerCase().includes(q);
      });
  }, [query.data, value, text]);

  // selected id → display info. 비활성/삭제된 skill 도 chip 으로 노출 (fallback UUID 표시).
  const idMeta = useMemo(() => {
    const map = new Map<string, { name: string; slug: string; description: string | null }>();
    for (const s of query.data?.skills ?? []) {
      map.set(s.id, { name: s.name, slug: s.slug, description: s.description });
    }
    return map;
  }, [query.data]);

  useEffect(() => {
    setActiveIdx((i) => (i >= candidates.length ? 0 : i));
  }, [candidates.length]);

  const capped = value.length >= max;
  const noSkills = (query.data?.skills.length ?? 0) === 0;

  const addId = (id: string) => {
    if (capped) return;
    if (value.includes(id)) return;
    onChange([...value, id]);
    setText('');
    setActiveIdx(0);
    inputRef.current?.focus();
  };

  const removeAt = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIdx((i) => (candidates.length === 0 ? 0 : Math.min(candidates.length - 1, i + 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = candidates[activeIdx];
      if (pick) addId(pick.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setText('');
    } else if (e.key === 'Backspace' && text.length === 0 && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => {
          if (!capped) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
        className={cn(
          'w-full text-left flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1.5 min-h-[2.5rem]',
          'focus-within:border-accent',
          capped && 'opacity-90',
        )}
      >
        {value.map((id, idx) => {
          const meta = idMeta.get(id);
          const label = meta?.name ?? id;
          const slugLabel = meta?.slug ?? id;
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-muted/60 px-2 py-0.5 text-sm"
              title={meta?.description ?? slugLabel}
            >
              <span className="truncate max-w-[200px]">{label}</span>
              <span className="text-xs text-foreground-muted">{slugLabel}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(idx);
                }}
                aria-label={`${label} 제거`}
                className="grid place-items-center rounded-sm text-foreground-muted hover:text-foreground hover:bg-surface-muted"
              >
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={text}
          disabled={capped || noSkills}
          onChange={(e) => {
            setText(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={
            noSkills
              ? '등록된 skill 이 없습니다.'
              : capped
                ? `최대 ${max}개 — 먼저 제거하세요.`
                : value.length === 0
                  ? '이름·slug 로 검색…'
                  : ''
          }
          className="flex-1 min-w-[160px] bg-transparent text-sm focus-visible:outline-none disabled:cursor-not-allowed"
        />
      </button>

      {open && !capped && !noSkills ? (
        <div className="absolute left-0 right-0 top-full mt-1 z-10 rounded-md border border-border bg-surface shadow-lg max-h-72 overflow-y-auto">
          {query.isLoading ? (
            <div className="px-3 py-2 text-sm text-foreground-muted">불러오는 중…</div>
          ) : candidates.length === 0 ? (
            <div className="px-3 py-2 text-sm text-foreground-muted">
              {text.trim() ? '검색 결과 없음.' : '추가할 후보가 없습니다.'}
            </div>
          ) : (
            candidates.map((s, idx) => {
              const active = idx === activeIdx;
              return (
                <button
                  key={s.id}
                  type="button"
                  onMouseDown={(e) => {
                    // mousedown 으로 처리 — onClick 은 input blur 가 먼저 일어나 dropdown 이
                    // 외부 클릭으로 닫히는 race 가 가끔 있음.
                    e.preventDefault();
                    addId(s.id);
                  }}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={cn(
                    'block w-full text-left px-3 py-2 text-sm border-b border-border last:border-b-0',
                    active ? 'bg-surface-muted' : 'hover:bg-surface-muted/60',
                  )}
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium">{s.name}</span>
                    <span className="text-xs text-foreground-muted">{s.slug}</span>
                  </div>
                  {s.description ? (
                    <div className="text-xs text-foreground-muted line-clamp-1 mt-0.5">
                      {s.description}
                    </div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
